import { apiClient, getAuthToken } from './client';
import { ApiError } from '../types';

const API_BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:5000';

// ─── DTOs (mirror src/BRG.Api/DTOs/HubResourceDtos.cs) ──────────────────────

export interface HubResource {
  hubResourceId: number;
  title: string;
  description: string | null;
  resourceType: number;
  resourceTypeLabel: string;
  centroFileId: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  mimeType: string | null;
  externalLink: string | null;
  isExpired: boolean;
  expiresAt: string | null;
  recordingDate: string | null;
  host: string | null;
  meetingType: string | null;
  durationMinutes: number | null;
  createdAt: string;
  updatedAt: string;
  createdById: number;
  createdByName: string | null;
  downloadAvailable: boolean;
}

export interface HubResourceListResponse {
  count: number;
  items: HubResource[];
}

export interface CreateHubResourceRequest {
  title: string;
  description?: string | null;
  resourceType: number;
  recordingDate?: string | null;
  host?: string | null;
  meetingType?: string | null;
  durationMinutes?: number | null;
  externalLink?: string | null;
  draftId?: string | null;
  fileName?: string | null;
}

export interface FileDraftResponse {
  draftId: string;
  fileName: string;
  extension: string | null;
  fileSizeInBytes: number | null;
  previewUrl: string | null;
}

export interface DownloadUrlResponse {
  hubResourceId: number;
  fileName: string | null;
  downloadUrl: string;
  expiresAt: string | null;
  source: 'centro' | 'external';
}

export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024; // 200 MB

export const ALLOWED_EXTENSIONS: ReadonlyArray<string> = [
  'pdf', 'docx', 'pptx', 'xlsx', 'mp4', 'mov', 'png', 'jpg', 'jpeg', 'gif',
];

export interface UploadProgressCallback {
  (loaded: number, total: number): void;
}

export interface UpdateHubResourceRequest {
  title?: string;
  description?: string | null;
  resourceType?: number;
  recordingDate?: string | null;
  host?: string | null;
  meetingType?: string | null;
  durationMinutes?: number | null;
  externalLink?: string | null;
}

export interface ListParams {
  search?: string;
  resourceType?: number;
  fileType?: string;
  showExpired?: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const RESOURCE_TYPES = [
  { value: 1,  label: 'Articles & Thought Leadership' },
  { value: 2,  label: 'Videos & Multimedia' },
  { value: 3,  label: 'Websites & Digital Hubs' },
  { value: 4,  label: 'Toolkits & Practical Guides' },
  { value: 5,  label: 'Training & Learning Modules' },
  { value: 6,  label: 'Research, Data & Reports' },
  { value: 7,  label: 'Policies, Frameworks & Standards' },
  { value: 8,  label: 'Identity-Specific Resources' },
  { value: 9,  label: 'Leadership & Change Management' },
  { value: 10, label: 'Community Engagement & Dialogue' },
  { value: 11, label: 'News, Current Events & Commentary' },
];

export const MEETING_RECORDING_TYPE = 12;

export const FILE_TYPE_FILTERS = [
  { value: '',      label: 'All File Types' },
  { value: 'docx',  label: 'DOCX' },
  { value: 'pdf',   label: 'PDF' },
  { value: 'pptx',  label: 'PPTX' },
  { value: 'xlsx',  label: 'XLSX' },
  { value: 'video', label: 'Video' },
  { value: 'image', label: 'Image' },
  { value: 'link',  label: 'Link' },
];

// ─── Multipart upload helper (XHR for progress events) ─────────────────────
// fetch() doesn't expose upload progress; use XHR for the file upload path so
// the React modal can render a real progress bar.

interface MultipartUploadOptions {
  path: string;
  method: 'POST' | 'PUT';
  formData: FormData;
  onProgress?: UploadProgressCallback;
  signal?: AbortSignal;
}

async function multipartUpload<T>(opts: MultipartUploadOptions, getToken: () => Promise<string>): Promise<T> {
  const token = await getToken();

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method, `${API_BASE_URL}${opts.path}`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    if (opts.onProgress && xhr.upload) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) opts.onProgress!(event.loaded, event.total);
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (xhr.status === 204 || !xhr.responseText) {
          resolve(undefined as unknown as T);
          return;
        }
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch (err) {
          reject(new ApiError('Invalid JSON response', xhr.status));
        }
      } else {
        let message = `HTTP ${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText) as { error?: { code?: string; message?: string } };
          if (body.error?.message) message = body.error.message;
        } catch { /* ignore */ }
        reject(new ApiError(message, xhr.status));
      }
    });

    xhr.addEventListener('error', () => reject(new ApiError('Network error', 0)));
    xhr.addEventListener('abort', () => reject(new ApiError('Upload cancelled', 0)));

    if (opts.signal) {
      opts.signal.addEventListener('abort', () => xhr.abort());
    }

    xhr.send(opts.formData);
  });
}

// ─── Client ─────────────────────────────────────────────────────────────────

export const hubResourcesApi = {
  list(params: ListParams = {}): Promise<HubResourceListResponse> {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.resourceType != null) q.set('resourceType', String(params.resourceType));
    if (params.fileType) q.set('fileType', params.fileType);
    if (params.showExpired) q.set('showExpired', 'true');
    const qs = q.toString();
    return apiClient.get<HubResourceListResponse>(`/api/hub-resources${qs ? `?${qs}` : ''}`);
  },

  getById(id: number): Promise<HubResource> {
    return apiClient.get<HubResource>(`/api/hub-resources/${id}`);
  },

  create(data: CreateHubResourceRequest): Promise<HubResource> {
    return apiClient.post<HubResource>('/api/hub-resources', data);
  },

  update(id: number, data: UpdateHubResourceRequest): Promise<HubResource> {
    return apiClient.put<HubResource>(`/api/hub-resources/${id}`, data);
  },

  delete(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/hub-resources/${id}`);
  },

  // ─── Centro file flows ────────────────────────────────────────────────────

  /** Upload a file as a Centro draft. Call on file pick (Add flow) BEFORE Save. */
  uploadDraft(file: File, onProgress?: UploadProgressCallback, signal?: AbortSignal): Promise<FileDraftResponse> {
    const fd = new FormData();
    fd.append('file', file);
    return multipartUpload<FileDraftResponse>(
      { path: '/api/hub-resources/file-drafts', method: 'POST', formData: fd, onProgress, signal },
      getAuthToken,
    );
  },

  /** Cancel a draft (modal close without save). Idempotent. */
  deleteDraft(draftId: string): Promise<void> {
    return apiClient.delete<void>(`/api/hub-resources/file-drafts/${draftId}`);
  },

  /** Replace the file on an existing resource (Edit flow). Entity-based. */
  replaceFile(id: number, file: File, onProgress?: UploadProgressCallback, signal?: AbortSignal): Promise<HubResource> {
    const fd = new FormData();
    fd.append('file', file);
    return multipartUpload<HubResource>(
      { path: `/api/hub-resources/${id}/file`, method: 'PUT', formData: fd, onProgress, signal },
      getAuthToken,
    );
  },

  /** Get a 5-min presigned download URL (or the external link for URL-only resources). */
  getDownloadUrl(id: number): Promise<DownloadUrlResponse> {
    return apiClient.get<DownloadUrlResponse>(`/api/hub-resources/${id}/download-url`);
  },
};

/** Client-side validation: returns null if OK, otherwise an error message. */
export function validateFileForUpload(file: File): string | null {
  if (file.size > MAX_UPLOAD_BYTES) {
    return `File too large. Maximum size is ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`;
  }
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}.`;
  }
  return null;
}
